/**
 * ReleasePanel (CHAP-593): the last step before Discovery sends. Release stays
 * disabled until the typed count is exactly the frozen count (releaseGate),
 * and the server checks the typed count again.
 *
 * Presentational on purpose: the page owns the state and the call, so the
 * gate can be rendered and tested on its own.
 */
import { releaseGate, shaPrefix, snapshotIsStale, type Dispatch } from './model.js';

export interface ReleasePanelProps {
  dispatch: Pick<Dispatch, 'id' | 'emailNo' | 'frozenCount' | 'copySha' | 'createdAt'> | null;
  typed: string;
  onTypedChange: (value: string) => void;
  onRelease: () => void;
  pending: boolean;
  error: string | null;
  /** Clock for the 24-hour snapshot warning; injectable for tests. */
  now?: number;
}

export function ReleasePanel({ dispatch, typed, onTypedChange, onRelease, pending, error, now }: ReleasePanelProps) {
  if (!dispatch) {
    return (
      <p className="text-sm text-neutral">
        No frozen batch is waiting. Prepare one first; release then asks you to type its count.
      </p>
    );
  }
  const gate = releaseGate(dispatch.frozenCount, typed);
  const stale = snapshotIsStale(dispatch.createdAt, now ?? Date.now());
  return (
    <div className="space-y-3 min-w-0">
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm min-w-0">
        <dt className="text-neutral">Email</dt>
        <dd className="text-navy font-medium">{dispatch.emailNo ?? 'unknown'}</dd>
        <dt className="text-neutral">Frozen count</dt>
        <dd className="text-navy font-bold tabular-nums" id="dz-frozen-count">{dispatch.frozenCount ?? 'unknown'}</dd>
        <dt className="text-neutral">Copy sha</dt>
        <dd className="font-mono text-xs break-all">{dispatch.copySha ? shaPrefix(dispatch.copySha) : 'not given'}</dd>
        <dt className="text-neutral">Dispatch</dt>
        <dd className="font-mono text-xs break-all">{dispatch.id}</dd>
      </dl>
      {stale && (
        <p role="alert" className="text-xs text-amber-800">
          This batch was frozen more than 24 hours ago, so release will refuse it. Prepare it again.
        </p>
      )}
      <label className="block min-w-0" htmlFor="dz-typed-count">
        <span className="text-sm font-medium text-navy">Type the frozen count to release</span>
      </label>
      <input
        id="dz-typed-count"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        value={typed}
        onChange={(e) => onTypedChange(e.target.value)}
        aria-describedby="dz-release-why"
        className="w-40 max-w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
      />
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <button
          id="dz-release"
          type="button"
          disabled={!gate.ok || pending}
          onClick={onRelease}
          className="px-4 py-2 rounded bg-orange text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {pending ? 'Releasing...' : `Release ${dispatch.frozenCount ?? ''} emails`}
        </button>
        <span id="dz-release-why" className="text-xs text-neutral">
          {gate.ok ? 'The count matches. The server checks every gate again before it sends.' : gate.reason}
        </span>
      </div>
      {error && (
        <div role="alert" className="border border-red-200 bg-red-50 text-red-800 text-sm rounded p-2 break-words">
          {error}
        </div>
      )}
    </div>
  );
}
